// src/config/standards/health.js
// National Health Education Standards (NHES), structured for your app.
// Domains = the 8 NHES standards. Grade bands mapped to your grade keys.
// Notes:
// - Descriptions are faithful paraphrases of the NHES performance indicators.
// - Codes follow the NHES convention: <standard>.<gradeband>.<indicator>
//   gradeband: 2 = PreK–2, 5 = Grades 3–5, 8 = Grades 6–8, 12 = Grades 9–12

export const HEALTH_STANDARDS = {
  "Health": {}
};

// ---------------------------
// Domain (Standard) labels
// ---------------------------
const D1 = "Standard 1: Core Concepts";
const D2 = "Standard 2: Analyzing Influences";
const D3 = "Standard 3: Accessing Valid Information & Services";
const D4 = "Standard 4: Interpersonal Communication";
const D5 = "Standard 5: Decision-Making";
const D6 = "Standard 6: Goal-Setting";
const D7 = "Standard 7: Health-Enhancing Behaviors";
const D8 = "Standard 8: Advocacy";

// ---------------------------
// PreK–Grade 2 (codes *.2.*)
// ---------------------------
const PK2 = {
  [D1]: [
    { id: "HE-PK2-1.2.1", code: "1.2.1", description: "Identify that healthy behaviors affect personal health." },
    { id: "HE-PK2-1.2.2", code: "1.2.2", description: "Recognize that health has multiple dimensions." },
    { id: "HE-PK2-1.2.3", code: "1.2.3", description: "Describe basic ways to prevent communicable diseases." },
    { id: "HE-PK2-1.2.4", code: "1.2.4", description: "List ways to prevent common childhood injuries." },
    { id: "HE-PK2-1.2.5", code: "1.2.5", description: "Explain why seeking health care can be important." }
  ],
  [D2]: [
    { id: "HE-PK2-2.2.1", code: "2.2.1", description: "Identify how family influences personal health habits." },
    { id: "HE-PK2-2.2.2", code: "2.2.2", description: "Identify how school can support personal health." },
    { id: "HE-PK2-2.2.3", code: "2.2.3", description: "Describe how media can influence health behaviors." }
  ],
  [D3]: [
    { id: "HE-PK2-3.2.1", code: "3.2.1", description: "Identify trusted adults and professionals who help with health." },
    { id: "HE-PK2-3.2.2", code: "3.2.2", description: "Identify ways to find school and community health helpers." }
  ],
  [D4]: [
    { id: "HE-PK2-4.2.1", code: "4.2.1", description: "Show healthy ways to express needs, wants, and feelings." },
    { id: "HE-PK2-4.2.2", code: "4.2.2", description: "Demonstrate listening skills that support health." },
    { id: "HE-PK2-4.2.3", code: "4.2.3", description: "Demonstrate ways to respond in unsafe or threatening situations." },
    { id: "HE-PK2-4.2.4", code: "4.2.4", description: "Demonstrate ways to tell a trusted adult if harmed or threatened." }
  ],
  [D5]: [
    { id: "HE-PK2-5.2.1", code: "5.2.1", description: "Identify times when a health-related decision is needed." },
    { id: "HE-PK2-5.2.2", code: "5.2.2", description: "Tell when help is needed to make a health decision." }
  ],
  [D6]: [
    { id: "HE-PK2-6.2.1", code: "6.2.1", description: "Name a short-term personal health goal and act toward it." },
    { id: "HE-PK2-6.2.2", code: "6.2.2", description: "Identify who can help with a personal health goal." }
  ],
  [D7]: [
    { id: "HE-PK2-7.2.1", code: "7.2.1", description: "Show healthy practices to maintain or improve health." },
    { id: "HE-PK2-7.2.2", code: "7.2.2", description: "Show behaviors that avoid or reduce health risks." }
  ],
  [D8]: [
    { id: "HE-PK2-8.2.1", code: "8.2.1", description: "Make requests that promote personal health." },
    { id: "HE-PK2-8.2.2", code: "8.2.2", description: "Encourage peers to make positive health choices." }
  ]
};

// ---------------------------
// Grades 3–5 (codes *.5.*)
// ---------------------------
const G35 = {
  [D1]: [
    { id: "HE-35-1.5.1", code: "1.5.1", description: "Describe how healthy behaviors relate to personal health." },
    { id: "HE-35-1.5.2", code: "1.5.2", description: "Identify examples of emotional, intellectual, physical, and social health." },
    { id: "HE-35-1.5.3", code: "1.5.3", description: "Describe how safe, healthy environments promote personal health." },
    { id: "HE-35-1.5.4", code: "1.5.4", description: "Describe ways to prevent common childhood injuries and health problems." },
    { id: "HE-35-1.5.5", code: "1.5.5", description: "Describe when it is important to seek health care." }
  ],
  [D2]: [
    { id: "HE-35-2.5.1", code: "2.5.1", description: "Describe how family influences personal health behaviors." },
    { id: "HE-35-2.5.2", code: "2.5.2", description: "Identify cultural influences on health practices and behaviors." },
    { id: "HE-35-2.5.3", code: "2.5.3", description: "Identify how peers can influence healthy and unhealthy choices." },
    { id: "HE-35-2.5.4", code: "2.5.4", description: "Describe how school and community support personal health." },
    { id: "HE-35-2.5.5", code: "2.5.5", description: "Explain how media influence thoughts, feelings, and behaviors." },
    { id: "HE-35-2.5.6", code: "2.5.6", description: "Describe ways technology can influence personal health." }
  ],
  [D3]: [
    { id: "HE-35-3.5.1", code: "3.5.1", description: "Identify traits of valid health information, products, and services." },
    { id: "HE-35-3.5.2", code: "3.5.2", description: "Find valid health resources at home, school, and in the community." }
  ],
  [D4]: [
    { id: "HE-35-4.5.1", code: "4.5.1", description: "Show effective verbal and nonverbal communication for health." },
    { id: "HE-35-4.5.2", code: "4.5.2", description: "Demonstrate refusal skills to avoid or reduce risks." },
    { id: "HE-35-4.5.3", code: "4.5.3", description: "Demonstrate nonviolent strategies to manage or resolve conflicts." },
    { id: "HE-35-4.5.4", code: "4.5.4", description: "Demonstrate how to ask for help to enhance personal health." }
  ],
  [D5]: [
    { id: "HE-35-5.5.1", code: "5.5.1", description: "Identify health-related situations that may need a decision." },
    { id: "HE-35-5.5.2", code: "5.5.2", description: "Analyze when assistance is needed for a health decision." },
    { id: "HE-35-5.5.3", code: "5.5.3", description: "List healthy options for health-related issues or problems." },
    { id: "HE-35-5.5.4", code: "5.5.4", description: "Predict possible outcomes for each option." },
    { id: "HE-35-5.5.5", code: "5.5.5", description: "Choose the healthy option when deciding." },
    { id: "HE-35-5.5.6", code: "5.5.6", description: "Describe the outcomes of a health-related decision." }
  ],
  [D6]: [
    { id: "HE-35-6.5.1", code: "6.5.1", description: "Set a personal health goal and track progress." },
    { id: "HE-35-6.5.2", code: "6.5.2", description: "Identify resources that help achieve a health goal." }
  ],
  [D7]: [
    { id: "HE-35-7.5.1", code: "7.5.1", description: "Identify responsible personal health behaviors." },
    { id: "HE-35-7.5.2", code: "7.5.2", description: "Demonstrate healthy practices to maintain or improve health." },
    { id: "HE-35-7.5.3", code: "7.5.3", description: "Demonstrate behaviors to avoid or reduce health risks." }
  ],
  [D8]: [
    { id: "HE-35-8.5.1", code: "8.5.1", description: "Express opinions and give accurate information about health." },
    { id: "HE-35-8.5.2", code: "8.5.2", description: "Encourage others to make positive, healthy choices." }
  ]
};

// ---------------------------
// Grades 6–8 (codes *.8.*)
// ---------------------------
const G68 = {
  [D1]: [
    { id: "HE-68-1.8.1", code: "1.8.1", description: "Analyze how healthy behaviors relate to personal health." },
    { id: "HE-68-1.8.2", code: "1.8.2", description: "Describe interrelationships among emotional, intellectual, physical, and social health in adolescence." },
    { id: "HE-68-1.8.3", code: "1.8.3", description: "Analyze how the environment affects personal health." },
    { id: "HE-68-1.8.4", code: "1.8.4", description: "Explain how family history can affect personal health." },
    { id: "HE-68-1.8.5", code: "1.8.5", description: "Describe ways to reduce or prevent injuries and adolescent health problems." },
    { id: "HE-68-1.8.6", code: "1.8.6", description: "Explain how appropriate health care promotes personal health." },
    { id: "HE-68-1.8.7", code: "1.8.7", description: "Describe benefits and barriers to practicing healthy behaviors." },
    { id: "HE-68-1.8.8", code: "1.8.8", description: "Examine likelihood of injury/illness when engaging in unhealthy behaviors." },
    { id: "HE-68-1.8.9", code: "1.8.9", description: "Examine potential seriousness of injury/illness from unhealthy behaviors." }
  ],
  [D2]: [
    { id: "HE-68-2.8.1", code: "2.8.1", description: "Explain how family influences adolescent health." },
    { id: "HE-68-2.8.2", code: "2.8.2", description: "Describe cultural influences on health beliefs and behaviors." },
    { id: "HE-68-2.8.3", code: "2.8.3", description: "Describe how peers influence healthy and unhealthy behaviors." },
    { id: "HE-68-2.8.4", code: "2.8.4", description: "Analyze how school and community influence health practices." },
    { id: "HE-68-2.8.5", code: "2.8.5", description: "Analyze how media messages influence health behaviors." },
    { id: "HE-68-2.8.6", code: "2.8.6", description: "Analyze influences of technology on personal and family health." },
    { id: "HE-68-2.8.7", code: "2.8.7", description: "Explain how perceived norms influence behaviors." },
    { id: "HE-68-2.8.8", code: "2.8.8", description: "Explain influence of values and beliefs on practices." },
    { id: "HE-68-2.8.9", code: "2.8.9", description: "Describe how some risk behaviors increase likelihood of other risks." },
    { id: "HE-68-2.8.10", code: "2.8.10", description: "Explain how school/public health policies influence promotion and prevention." }
  ],
  [D3]: [
    { id: "HE-68-3.8.1", code: "3.8.1", description: "Analyze validity of health information, products, and services." },
    { id: "HE-68-3.8.2", code: "3.8.2", description: "Access valid health information from home, school, and community." },
    { id: "HE-68-3.8.3", code: "3.8.3", description: "Determine accessibility of products that enhance health." },
    { id: "HE-68-3.8.4", code: "3.8.4", description: "Describe situations that may require professional services." },
    { id: "HE-68-3.8.5", code: "3.8.5", description: "Locate valid and reliable health products and services." }
  ],
  [D4]: [
    { id: "HE-68-4.8.1", code: "4.8.1", description: "Apply effective verbal and nonverbal communication skills." },
    { id: "HE-68-4.8.2", code: "4.8.2", description: "Demonstrate refusal and negotiation skills to avoid/reduce risks." },
    { id: "HE-68-4.8.3", code: "4.8.3", description: "Demonstrate effective conflict management/resolution strategies." },
    { id: "HE-68-4.8.4", code: "4.8.4", description: "Demonstrate how to ask for assistance for self and others." }
  ],
  [D5]: [
    { id: "HE-68-5.8.1", code: "5.8.1", description: "Identify conditions that help or hinder healthy decisions." },
    { id: "HE-68-5.8.2", code: "5.8.2", description: "Decide when a thoughtful decision-making process is needed." },
    { id: "HE-68-5.8.3", code: "5.8.3", description: "Distinguish individual vs. collaborative decision making." },
    { id: "HE-68-5.8.4", code: "5.8.4", description: "Differentiate healthy vs. unhealthy alternatives." },
    { id: "HE-68-5.8.5", code: "5.8.5", description: "Predict short-term impacts of each alternative." },
    { id: "HE-68-5.8.6", code: "5.8.6", description: "Choose the healthy alternative when deciding." },
    { id: "HE-68-5.8.7", code: "5.8.7", description: "Analyze outcomes of a health-related decision." }
  ],
  [D6]: [
    { id: "HE-68-6.8.1", code: "6.8.1", description: "Assess personal health practices." },
    { id: "HE-68-6.8.2", code: "6.8.2", description: "Develop a goal to adopt, maintain, or improve a practice." },
    { id: "HE-68-6.8.3", code: "6.8.3", description: "Apply strategies and skills to achieve a health goal." },
    { id: "HE-68-6.8.4", code: "6.8.4", description: "Describe how goals can vary with changing responsibilities." }
  ],
  [D7]: [
    { id: "HE-68-7.8.1", code: "7.8.1", description: "Explain the importance of personal responsibility for health." },
    { id: "HE-68-7.8.2", code: "7.8.2", description: "Demonstrate healthy practices that support self and others." },
    { id: "HE-68-7.8.3", code: "7.8.3", description: "Demonstrate behaviors to avoid or reduce risks to self/others." }
  ],
  [D8]: [
    { id: "HE-68-8.8.1", code: "8.8.1", description: "State a health-enhancing position and support it with evidence." },
    { id: "HE-68-8.8.2", code: "8.8.2", description: "Influence and support others to make healthy choices." },
    { id: "HE-68-8.8.3", code: "8.8.3", description: "Work cooperatively to advocate for healthy schools and families." },
    { id: "HE-68-8.8.4", code: "8.8.4", description: "Adapt health messages and techniques for different audiences." }
  ]
};

// ---------------------------
// Grades 9–12 (codes *.12.*)
// ---------------------------
const G912 = {
  [D1]: [
    { id: "HE-912-1.12.1", code: "1.12.1", description: "Predict how healthy behaviors can affect health status." },
    { id: "HE-912-1.12.2", code: "1.12.2", description: "Describe interrelationships among all dimensions of health." },
    { id: "HE-912-1.12.3", code: "1.12.3", description: "Analyze how environment and personal health are interrelated." },
    { id: "HE-912-1.12.4", code: "1.12.4", description: "Analyze how genetics and family history can impact health." },
    { id: "HE-912-1.12.5", code: "1.12.5", description: "Propose ways to reduce or prevent injuries and health problems." },
    { id: "HE-912-1.12.6", code: "1.12.6", description: "Analyze how access to health care relates to health status." },
    { id: "HE-912-1.12.7", code: "1.12.7", description: "Compare benefits and barriers to practicing healthy behaviors." },
    { id: "HE-912-1.12.8", code: "1.12.8", description: "Analyze personal susceptibility to injury, illness, or death if engaging in unhealthy behaviors." },
    { id: "HE-912-1.12.9", code: "1.12.9", description: "Analyze potential severity of injury or illness from unhealthy behaviors." }
  ],
  [D2]: [
    { id: "HE-912-2.12.1", code: "2.12.1", description: "Analyze how family influences the health of individuals." },
    { id: "HE-912-2.12.2", code: "2.12.2", description: "Analyze how culture supports or challenges health beliefs and practices." },
    { id: "HE-912-2.12.3", code: "2.12.3", description: "Analyze how peers influence healthy and unhealthy behaviors." },
    { id: "HE-912-2.12.4", code: "2.12.4", description: "Evaluate how school and community affect health practices." },
    { id: "HE-912-2.12.5", code: "2.12.5", description: "Evaluate the effect of media on personal and family health." },
    { id: "HE-912-2.12.6", code: "2.12.6", description: "Evaluate the impact of technology on personal, family, and community health." },
    { id: "HE-912-2.12.7", code: "2.12.7", description: "Analyze how perceived norms influence behaviors." },
    { id: "HE-912-2.12.8", code: "2.12.8", description: "Analyze how personal values and beliefs influence practices." },
    { id: "HE-912-2.12.9", code: "2.12.9", description: "Analyze how some risk behaviors increase likelihood of other risks." },
    { id: "HE-912-2.12.10", code: "2.12.10", description: "Analyze how public policies and regulations influence promotion and prevention." }
  ],
  [D3]: [
    { id: "HE-912-3.12.1", code: "3.12.1", description: "Evaluate the validity of health information, products, and services." },
    { id: "HE-912-3.12.2", code: "3.12.2", description: "Use valid home, school, and community resources for health information." },
    { id: "HE-912-3.12.3", code: "3.12.3", description: "Determine accessibility of products and services that enhance health." },
    { id: "HE-912-3.12.4", code: "3.12.4", description: "Determine when professional health services may be required." },
    { id: "HE-912-3.12.5", code: "3.12.5", description: "Access valid, reliable health products and services." }
  ],
  [D4]: [
    { id: "HE-912-4.12.1", code: "4.12.1", description: "Use effective communication with family, peers, and others to enhance health." },
    { id: "HE-912-4.12.2", code: "4.12.2", description: "Demonstrate refusal, negotiation, and collaboration skills to avoid/reduce risks." },
    { id: "HE-912-4.12.3", code: "4.12.3", description: "Use strategies to prevent, manage, or resolve conflicts without harm." },
    { id: "HE-912-4.12.4", code: "4.12.4", description: "Ask for and offer assistance to enhance health for self and others." }
  ],
  [D5]: [
    { id: "HE-912-5.12.1", code: "5.12.1", description: "Examine barriers that can hinder healthy decision making." },
    { id: "HE-912-5.12.2", code: "5.12.2", description: "Determine the value of a thoughtful decision-making process." },
    { id: "HE-912-5.12.3", code: "5.12.3", description: "Justify when individual or collaborative decisions are appropriate." },
    { id: "HE-912-5.12.4", code: "5.12.4", description: "Generate alternatives for health-related issues or problems." },
    { id: "HE-912-5.12.5", code: "5.12.5", description: "Predict short- and long-term impacts of each alternative." },
    { id: "HE-912-5.12.6", code: "5.12.6", description: "Defend the healthy choice when making decisions." },
    { id: "HE-912-5.12.7", code: "5.12.7", description: "Evaluate the effectiveness of health-related decisions." }
  ],
  [D6]: [
    { id: "HE-912-6.12.1", code: "6.12.1", description: "Assess personal health practices and overall status." },
    { id: "HE-912-6.12.2", code: "6.12.2", description: "Develop a plan to achieve a personal health goal based on strengths, needs, and risks." },
    { id: "HE-912-6.12.3", code: "6.12.3", description: "Implement strategies and monitor progress toward a health goal." },
    { id: "HE-912-6.12.4", code: "6.12.4", description: "Formulate an effective long-term personal health plan." }
  ],
  [D7]: [
    { id: "HE-912-7.12.1", code: "7.12.1", description: "Analyze the role of individual responsibility in enhancing health." },
    { id: "HE-912-7.12.2", code: "7.12.2", description: "Demonstrate healthy practices to improve health of self and others." },
    { id: "HE-912-7.12.3", code: "7.12.3", description: "Demonstrate behaviors to avoid or reduce risks to self and others." }
  ],
  [D8]: [
    { id: "HE-912-8.12.1", code: "8.12.1", description: "Use accurate peer/societal norms to craft a health-enhancing message." },
    { id: "HE-912-8.12.2", code: "8.12.2", description: "Influence and support others to make healthy choices." },
    { id: "HE-912-8.12.3", code: "8.12.3", description: "Work cooperatively to advocate for personal, family, and community health." },
    { id: "HE-912-8.12.4", code: "8.12.4", description: "Adapt health messages and techniques to specific audiences." }
  ]
};

// -----------------------------------------
// Wire band objects to your per-grade keys
// -----------------------------------------
const HEALTH_MAP = {
  "Kindergarten": PK2,
  "1st Grade": PK2,
  "2nd Grade": PK2,
  "3rd Grade": G35,
  "4th Grade": G35,
  "5th Grade": G35,
  "6th Grade": G68,
  "7th Grade": G68,
  "8th Grade": G68,
  "High School": G912,
  "9th Grade": G912,
  "10th Grade": G912,
  "11th Grade": G912,
  "12th Grade": G912
};

HEALTH_STANDARDS["Health"] = HEALTH_MAP;

export default HEALTH_STANDARDS;
